import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import FilePlayer from 'react-player/file';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { MediaBlock } from 'stile-shared/src/components/2020/blocks/MediaBlock';
import { TeaserBlock } from 'stile-shared/src/components/2020/blocks/TeaserBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { Link } from 'stile-shared/src/components/2020/common/Link';
import { P, Section } from 'stile-shared/src/components/2020/common/Primitives';
import { VideoWrapper } from 'stile-shared/src/components/2020/common/VideoWrapper';

import { getPageData } from 'utils/getPageData';

import { PageLayout } from 'templates/2020/PageLayout';

import { NextPageBlock } from 'components/blocks/NextPageBlock';

import timelapse from '../../../../stile-shared/assets/videos/stile_x_timelapse.mp4';

export { Head } from 'templates/2020/Head';

function ArtOfSciencePage(props: PageProps) {
  /* 
  NOTE: this page has a student variation of the page found at /art-of-science/ 
  and should be maintained alongside that page 
  */

  const { hero } = getPageData(props.location.pathname);

  return (
    <PageLayout {...props}>
      <HeroBlock
        {...hero}
        image={
          <StaticImage
            loading="eager"
            src="../../../../stile-shared/assets/images/steam/cover_steam_whale.png"
            alt="An artwork featuring a whale composed of outlined rubbish items, and a background that appears like it is underwater"
          />
        }
      />

      <TextBlock>
        <P>
          We want to make science interesting and accessible for every student. We aim to do this by
          creating lessons that contextualise learning with real-world, socio-scientific issues that
          engage students in science. {' '}
        </P>
        <P>
          Throughout our collection, you’ll find ‘Art of Science’ posters covering important
          scientific issues that have inspired one of our in-house artists. We hope that they’ll
          spark an interest in your students, prompt a discussion around the issue and boost their
          visual literacy skills.
        </P>
      </TextBlock>

      <Section>
        <MediaBlock as="div" layout="step-repeat-four-left">
          <StaticImage
            src="../../../../stile-shared/assets/images/steam/crispr.png"
            alt="An illustration featuring golden scissors with golden portraits of Emmanuelle Charpentier and Jennifer Doudna"
          />
          <StaticImage
            src="../../../../stile-shared/assets/images/steam/animals.png"
            alt="An illustration featuring several endangered animals, such as an elephant, a gorilla, a tiger and a shark"
          />
          <StaticImage
            src="../../../../stile-shared/assets/images/steam/bee.png"
            alt="An illustration of a lifelike bee, crumbling into dust"
          />
          <StaticImage
            src="../../../../stile-shared/assets/images/steam/disease.png"
            alt="An illustration of an enourmous purple disease cell, with various scientists standing on it, researching and communicating their findings"
          />
        </MediaBlock>
      </Section>

      <TeaserBlock
        title="The Art of Stile X"
        text="Take a closer look at the front cover of your Stile X booklets. Each unique cover was lovingly crafted by one of our in-house artists to match the scientific concepts in each unit."
        variant="dark"
        image={
          <VideoWrapper>
            <FilePlayer url={timelapse} width="100%" height="100%" playing playsinline loop muted />
          </VideoWrapper>
        }
      ></TeaserBlock>

      <TextBlock variant="dark">
        <P>
          If you haven’t heard about Stile X before, it’s a collection of forty printed booklets
          that scaffold best practice note-taking while supporting students to implement effective
          study and revision skills. These booklets contain entirely new science content for the
          bits done best on paper.
        </P>
        <P>
          <Link variant="inherit" url="/what-is-stile/stile-x/">
            Find out more Stile X
          </Link>
          .
        </P>
      </TextBlock>

      <NextPageBlock path="/why-choose-stile/literacy-and-numeracy/" />
      <CtaBlock />
    </PageLayout>
  );
}

export default ArtOfSciencePage;
